import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { GrainTrackerForm, theBinIsGood } from "./GrainTrackerForm";
import { Alert, Button, FormControl, Tooltip } from "@mui/material";
import { State } from "../../../redux/slices";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import {
  fetchSpeciesTemplate,
  fetchStoragesOfEachLocation,
  fetchStorageTemplate,
  fetchQualityCriteria,
} from "../../../redux/actions/GrainWise/grainTrackerGetServices";
import {
  CreateLocationStep,
  setCreateLocationStep,
} from "../../../redux/slices/location/createLocation";

import { addNewBin } from "../../../redux/actions/GrainWise/grainTracker";

import {
  selecteBinIndex,
  setAllotmentPolicies,
  setLocationGrainTracker,
  setPrincipalInfoGT,
  setPrincipalInfoGTCopy,
  setTheBinIsGood,
  resetAdvancedDeclarationOptionAndDeclarationOptions,
  resetMessagesDone,
} from "../../../redux/slices/grainTracker/grainTracker";
import { ShowsMessagesSuccess } from "../partsOfForm/GrainTrackerSuccessMessages";
import { PrincipalInfoGT } from "../../../redux/slices/grainTracker/grainTrackerTypes";
import GrainTrackerLayoutForm from "../partsOfForm/grainwiseLayout.tsx/GrainWiseStoragesLayout";
import { SetActionsLayout } from "../../layout/Layout";
import { CheckExpiredToken } from "../../../utils/authentication";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    fetchStoragesOfEachLocation: (token: string, orgGeoPointId: number) => {
      dispatch(fetchStoragesOfEachLocation({ token, orgGeoPointId }));
      dispatch(fetchStorageTemplate(token));
      dispatch(fetchSpeciesTemplate(token));
      dispatch(resetMessagesDone());
      dispatch(resetAdvancedDeclarationOptionAndDeclarationOptions());
    },

    setSteps: (value: CreateLocationStep) => {
      dispatch(setCreateLocationStep(value));
    },

    setLocationGrainTracker: (graintracker: any) => {
      dispatch(setLocationGrainTracker(graintracker));
    },

    binCreation: (token: string) => {
      dispatch(addNewBin(token));
    },

    selectBinIndex: (index: number) => {
      dispatch(selecteBinIndex(index));
    },

    setTheBinIsGood: (value: boolean) => {
      dispatch(setTheBinIsGood(value));
    },

    setInformationByClicking: (
      binInformation: any,
      principalInfoGT: PrincipalInfoGT,
      principalInfoGTCopy: PrincipalInfoGT,
      token: string
    ) => {
      dispatch(
        setTheBinIsGood(
          !theBinIsGood(
            binInformation.cerealTypeId,
            binInformation.storageTemplateId,
            binInformation.totalCapacity
              ? binInformation.totalCapacity.value
              : 0
          )
        )
      );

      dispatch(
        setPrincipalInfoGT({
          ...principalInfoGT,
          name: binInformation.name,
          specie: binInformation.cerealTypeId,
          storage: binInformation.storageTemplateId,
          currentCharacteristics: binInformation.currentCharacteristics,
          allotmentPolicies: binInformation.allotmentPolicies,
          tonsCapacity:
            binInformation?.characteristics?.capacityMetricTonsWheatEq,
          id: binInformation.id,
        })
      );

      dispatch(
        setPrincipalInfoGTCopy({
          ...principalInfoGTCopy,
          name: binInformation.name,
          specie: binInformation.cerealTypeId,
          currentCharacteristics: binInformation.currentCharacteristics,
          storage: binInformation.storageTemplateId,
          allotmentPolicies: binInformation.allotmentPolicies,
          tonsCapacity:
            binInformation?.characteristics?.capacityMetricTonsWheatEq,
          id: binInformation.id,
        })
      );
      dispatch(setAllotmentPolicies([]));
      setTimeout(() => {
        dispatch(setAllotmentPolicies(binInformation.allotmentPolicies));
      }, 300);

      dispatch(
        fetchQualityCriteria({ token, specie: binInformation.cerealTypeId })
      );
    },
  };
};

export const GrainTrackerStock: FC = () => {
  const [t] = useTranslation("global");
  const { user } = useSelector((state: State) => state.Authentication);

  const {
    locationGrainTrackerValues,
    listOfStoragesTemplates,
    listOfSpecies,
    editBinAllotment,
    editBins,
    deleteAllotmentPolicies,
    principalInfoGT,
    principalInfoGTCopy,
    selectIndexLayoutBin,
  } = useSelector((state: State) => state.GrainWiseReducer);

  const { createLocationStep } = useSelector(
    (state: State) => state.CreateLocationReducer
  );

  const token = user?.token ?? "";

  const {
    fetchStoragesOfEachLocation,
    setSteps,
    setLocationGrainTracker,
    setInformationByClicking,
    binCreation,
    selectBinIndex,
  } = SetActions();
  const { logoutClickHandler } = SetActionsLayout();

  const errorStoragesTemplateEmpty = () => {
    if (listOfStoragesTemplates.storagesTemplates?.data.length === 0) {
      return "Not storages templates";
    } else {
      return "";
    }
  };

  const sumOfErrors = locationGrainTrackerValues?.error?.concat(
    listOfStoragesTemplates?.error?.concat(
      listOfSpecies?.error.concat(errorStoragesTemplateEmpty())
    )
  );

  const { isAddingStorage, isEditingBin, storagesList } =
    locationGrainTrackerValues;

  useEffect(() => {
    fetchStoragesOfEachLocation(
      token,
      locationGrainTrackerValues.locationInformation?.id as number
    );

    CheckExpiredToken(logoutClickHandler, user);
  }, []);

  const managingRedirectUser = () => {
    setSteps({ ...createLocationStep, step: 3 });
  };

  return (
    <div className="steps-container-create-loc">
      <div className="background-graintracker" />
      <div className="step-title">{`${t("Common_options.step")} 2/3`} </div>

      <div className="step-title-create">
        {t("Grain_tracker.storage_title")}
      </div>
      <div className="step-subtitle-grain">
        {t("Grain_tracker.storage_subtitle")}
      </div>
      {sumOfErrors?.length >= 1 ? (
        <div>
          <Alert severity="error">{sumOfErrors}</Alert>
        </div>
      ) : (
        ""
      )}

      <div className="graintracker-form-container mt-3 mb-3">
        <GrainTrackerLayoutForm
          t={t}
          setLocationGrainTracker={setLocationGrainTracker}
          setInformationByClicking={setInformationByClicking}
          binCreation={binCreation}
          selectBinIndex={selectBinIndex}
          locationGrainTrackerValues={locationGrainTrackerValues}
          principalInfoGT={principalInfoGT}
          principalInfoGTCopy={principalInfoGTCopy}
          selectIndexLayoutBin={selectIndexLayoutBin}
          listOfStoragesTemplates={listOfStoragesTemplates}
          editBins={editBins}
          token={token}
        />
        {GrainTrackerForm(t)}
      </div>

      <Tooltip
        title={
          isEditingBin || isAddingStorage || storagesList.length === 0
            ? t("Grain_tracker.to_continue")
            : ""
        }
        placement="right"
      >
        <FormControl>
          <Button
            sx={{ textTransform: "none" }}
            disabled={
              isEditingBin || isAddingStorage || storagesList.length === 0
            }
            onClick={() => managingRedirectUser()}
            variant="contained"
          >
            {t("Grain_tracker.continue-button")}
          </Button>
        </FormControl>
      </Tooltip>

      {ShowsMessagesSuccess(
        t,
        editBinAllotment,
        editBins,
        deleteAllotmentPolicies
      )}
    </div>
  );
};
